<template>
  <div id="subs-report-scroll-wrap" class="page-content">
    <section class="filter-wrap">
      <ReportFilter
        :filter="
          filter.form
        "
        :open-panel.sync="
          filter.panel
        "
        :disabled="
          loader
        "
        @search="
          handleSearch
        "
        @error="
          handleError
        "
      />
    </section>

    <section class="summary-wrap">
      <ReportSummary
        :filter="
          filter.form
        "
        @error="
          handleError
        "
      />
    </section>

    <section class="table-wrap">
      <ReportTable
        :loader="
          loader
        "
        :total="
          list.total
        "
        :pager="
          filter.paging
        "
        :rows="
          rows
        "
        :items="
          list.items
        "
        @page-change="
          handlePageChange
        "
        @limit-change="
          handleLimitChange
        ">
        <FilterTags
          slot="tags"
          :filter="
            filter.form
          "
          :disabled="
            loader
          "
          @click="
            () => filter.panel = true
          "
          @remove="
            handleRemove
          "
        />
        <ReportExport
          v-if="
            list.total > 0
          "
          slot="actions"
          :filter="
            filter.form
          "
          :loader.sync="
            loader
          "
          @error="
            handleError
          "
        />
      </ReportTable>
    </section>

    <!-- Loader -->
    <div
      v-loading.fullscreen.lock="
        loader
      "
      style="
        position: absolute;
      "
      element-loading-background="
        rgba(0, 0, 0, 0.2)
      ">
    </div>

    <!-- Alert -->
    <ReportAlert
      :open="
        alert.open
      "
      :message="
        alert.message
      "
      @closed="
        () => alert.open = false
      "
    />
  </div>
</template>
<script lang="js">

// Client
import { SubscriberReportClient as Client } from '@/clients/BackendAdminClient/subscriber-report'

// Common
import { SubsReportFilterCommon as FilterCommon } from '@/features/subscribers-report/common/subs-report-filter.common'
import { SubsReportMapperCommon as MapperCommon } from '@/features/subscribers-report/common/subs-report-mapper.common'

// Components
import FilterTags from '@/features/subscribers-report/components/SubsReportFilterTags'
import ReportAlert from '@/features/subscribers-report/components/SubsReportAlert'
import ReportFilter from './SubsReportFilter'
import ReportSummary from './SubsReportSummary'
import ReportTable from './SubsReportTable'
import ReportExport from './SubsReportExport'

// Common filter
const { filter, pager } = FilterCommon

export default {
  components: {
    ReportFilter,
    ReportSummary,
    ReportTable,
    FilterTags,
    ReportExport,
    ReportAlert,
  },
  data() {
    return {
      loader: false,
      fetchToken: 0,
      filter: {
        form  : filter(),
        panel : false,
        paging: pager(30),
      },
      list: {
        total: 0,
        items: []
      },
      alert: {
        open    : false,
        message : null
      }
    }
  },
  computed: {
    rows() {
      return this.list.items.map(
        item => MapperCommon.toRow(item)
      )
    }
  },
  created() {

    // Init from query params
    this.setup()

    // Set query params
    this.syncQuery()
  },
  methods: {
    setup() {

      // Parse Query
      const { page, limit, ...form } = (
        FilterCommon.fromQuery(this.$route.query)
      )

      // Set Form
      this.filter.form = form

      // Set Pager
      this.filter.paging = { page, limit }

      // Fetch
      this.fetch()
    },
    syncQuery() {
      this.pushQuery(
        this.filter.form, this.filter.paging
      )
    },
    handleSearch(form) {

      // Reset to first page on filter change
      this.filter.form = form

      // Pager props
      const { page } = pager()

      // Set pager
      this.filter.paging = {
        ...this.filter.paging, page
      }

      // Fetch
      this.fetch()

      // Sync URL, for bookmarking only
      this.pushQuery(
        this.filter.form, this.filter.paging
      )
    },
    handleRemove(key) {
      this.handleSearch(
        FilterCommon.removeField(this.filter.form, key)
      )
    },
    handlePageChange(page) {

      // Set pager
      this.filter.paging = {
        ...this.filter.paging, page
      }

      // Fetch
      this.fetch()

      // Sync URL
      this.pushQuery(
        this.filter.form, this.filter.paging
      )
    },
    handleLimitChange(limit) {

      // Reset to first page on limit change
      const { page } = pager()

      // Set pager
      this.filter.paging = {
        page, limit
      }

      // Fetch
      this.fetch()

      // Sync URL
      this.pushQuery(
        this.filter.form, this.filter.paging
      )
    },
    async pushQuery(form, paging) {

      // Route props
      const { name } = this.$route

      // Set query params
      const query = (
        FilterCommon.toQuery(form, paging)
      )

      try {
        await this.$router.replace({
          name, query
        })
      }
      catch (error) {
        if (error.name !== 'NavigationDuplicated') throw error
      }
    },
    async fetch() {

      // Validate filter
      if (!this.filter.form) return

      // Guards against stale responses
      this.fetchToken += 1
      const token = this.fetchToken

      try {

        // Loading
        this.loader = true

        // Request Report List
        const list = await Client.list({
          ...this.filter.form,
          ...this.filter.paging
        })

        // Discard stale response
        if (token !== this.fetchToken) return

        // Set List
        this.list = list

        // List props
        const { items } = this.list

        // Filter props
        const { paging } = this.filter

        // Step back one page when current page has no items
        if (!items.length && paging.page > 1) {

          // Set paging
          this.filter.paging = {
            ...paging, page: paging.page - 1
          }

          // Set query params
          this.pushQuery(
            this.filter.form, this.filter.paging
          )

          // Refresh report
          await this.fetch()
          return
        }

        // Scroll to top
        this.scrollToTop()
      }
      catch (error) {

        // Discard stale error
        if (token !== this.fetchToken) return

        // Fetch error
        this.handleError(
          error, 'ไม่สามารถโหลดรายงานได้'
        )
      }
      finally {

        // Discard stale loader toggle
        if (token === this.fetchToken) {
          this.loader = false
        }
      }
    },
    handleError(error, message) {

      // Log error
      console.error(
        '[subs-report-list]', error
      )

      // Notify user
      this.notify(message)
    },
    notify(message) {

      // Validate Message
      if (!message) return

      // Set Alert
      this.alert = {
        open: true, message
      }
    },
    scrollToTop() {

      // Scroll wrap
      const el = document.getElementById(
        'subs-report-scroll-wrap'
      )

      // Validate wrap
      if (!el) return

      // Scroll top
      el.scrollTo({
        top: 0, behavior: 'smooth'
      })
    },
  }
}

</script>

<style lang="css" scoped>

.page-content {
  display: unset !important;
  gap    : var(--size-md);
}

.page-content section {
  margin-bottom: var(--size-md);
}

.filter-wrap {
  flex      : none;
  margin-top: var(--size-md);
}

.summary-wrap {
  flex: none;
}

.table-wrap {
  /* flex      : 1;
  max-height: 800px; */
  width     : 90%;
  min-height: 0;
  overflow  : auto;
  margin    : 0 auto;
}

</style>